/**
 * Path segment of &lt;base href&gt; (e.g. `/reports` or from a full URL).
 */
function basePathPrefixFromHref(href: string): string {
  const trimmed = href.replace(/\/+$/, '') || '/';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const pathname = new URL(trimmed.endsWith('/') ? trimmed : `${trimmed}/`)
        .pathname;
      return pathname.replace(/\/+$/, '') || '';
    } catch {
      return trimmed;
    }
  }
  return trimmed === '/' ? '' : trimmed;
}

/**
 * Prefix app-root-relative paths with the document <base href> when the app is
 * served under a path (e.g. parent CloudFront `/reports/...`). Leaves http(s), //, data, blob unchanged.
 */
export function withAppBaseHref(pathFromAppRoot: string): string {
  if (!pathFromAppRoot) {
    return pathFromAppRoot;
  }
  if (
    /^https?:\/\//i.test(pathFromAppRoot) ||
    pathFromAppRoot.startsWith('//') ||
    pathFromAppRoot.startsWith('data:') ||
    pathFromAppRoot.startsWith('blob:')
  ) {
    return pathFromAppRoot;
  }
  const q = pathFromAppRoot.indexOf('?');
  const pathOnly = q >= 0 ? pathFromAppRoot.slice(0, q) : pathFromAppRoot;
  const query = q >= 0 ? pathFromAppRoot.slice(q) : '';
  const normalized = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const baseAttr =
    (typeof document !== 'undefined' &&
      document.querySelector('base')?.getAttribute('href')) ||
    '/';
  if (!baseAttr || baseAttr === '/') {
    return `${normalized}${query}`;
  }
  const basePath = basePathPrefixFromHref(baseAttr);
  if (!basePath) {
    return `${normalized}${query}`;
  }
  if (normalized === basePath || normalized.startsWith(`${basePath}/`)) {
    return `${normalized}${query}`;
  }
  return `${basePath}${normalized}${query}`;
}
